/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Error Alert - Prominent error message with retry option
 * Updated: 2026-01-09
 */

import React, { useState } from 'react';
import { AlertTriangle, X, ChevronDown } from 'lucide-react';

interface ErrorAlertProps {
  message: string;
  onDismiss: () => void;
  onRetry?: () => Promise<void>;
  details?: string;
}

const ErrorAlert: React.FC<ErrorAlertProps> = ({
  message,
  onDismiss,
  onRetry,
  details,
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    if (!onRetry) return;
    setIsRetrying(true);
    try {
      await onRetry();
    } catch (err) {
      console.error('Retry failed:', err);
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-top-2 duration-300 bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-lg p-4 shadow-sm">
      {/* Header */}
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-900">{message}</p>
        </div>
        <button
          onClick={onDismiss}
          className="flex-shrink-0 p-1 hover:bg-red-100 rounded transition-colors"
          title="Dismiss"
        >
          <X className="w-4 h-4 text-red-600" />
        </button>
      </div>

      {/* Details (expandable) */}
      {details && (
        <div className="mt-3 ml-8">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs text-red-700 hover:text-red-900 font-medium flex items-center gap-1 transition-colors"
          >
            {showDetails ? 'Hide' : 'Show'} details
            <ChevronDown
              className={`w-3 h-3 transition-transform ${
                showDetails ? 'rotate-180' : ''
              }`}
            />
          </button>
          {showDetails && (
            <pre className="mt-2 text-xs text-red-700 bg-white rounded p-2 border border-red-100 overflow-auto max-h-40">
              {details}
            </pre>
          )}
        </div>
      )}

      {/* Actions */}
      {(onRetry || onDismiss) && (
        <div className="mt-3 ml-8 flex gap-2">
          {onRetry && (
            <button
              onClick={handleRetry}
              disabled={isRetrying}
              className="px-3 py-1.5 text-xs font-semibold bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded transition-colors"
            >
              {isRetrying ? 'Retrying...' : 'Retry'}
            </button>
          )}
          <button
            onClick={onDismiss}
            className="px-3 py-1.5 text-xs font-medium bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
};

export default ErrorAlert;
